import { useMutation } from "@tanstack/react-query";
import { Cpu } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { AuthApi } from "@/lib/api";
import { useAuth } from "@/lib/auth";

type Mode = "login" | "register";

export function LoginPage() {
  const [mode, setMode] = useState<Mode>("login");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const setSession = useAuth((s) => s.setSession);

  const submit = useMutation({
    mutationFn: async () => {
      const tokenResp =
        mode === "login"
          ? await AuthApi.login(username, password)
          : await AuthApi.register(username, password, displayName || undefined);
      // Apply token before fetching /me
      useAuth.setState({ token: tokenResp.access_token });
      const user = await AuthApi.me();
      setSession(tokenResp.access_token, user);
      navigate("/projects", { replace: true });
    },
    onError: (err: unknown) => {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setError(detail ?? "Authentication failed");
    },
  });

  return (
    <div className="flex min-h-screen items-center justify-center bg-bg p-6">
      <div className="w-full max-w-sm space-y-6 rounded-xl border border-border bg-bg-surface p-8">
        <div className="flex flex-col items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-accent to-accent-hover">
            <Cpu size={24} className="text-bg" />
          </div>
          <div className="text-center">
            <h1 className="text-lg font-semibold">DroneTag</h1>
            <p className="text-sm text-text-muted">Audio Labeling Platform</p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-1 rounded-md border border-border bg-bg p-1 text-sm">
          {(["login", "register"] as Mode[]).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => {
                setMode(m);
                setError(null);
              }}
              className={
                mode === m
                  ? "rounded bg-bg-elevated px-3 py-1.5 text-text"
                  : "rounded px-3 py-1.5 text-text-muted hover:text-text"
              }
            >
              {m === "login" ? "Sign in" : "Register"}
            </button>
          ))}
        </div>

        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            setError(null);
            submit.mutate();
          }}
        >
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-text-muted">Username</label>
            <input
              className="input"
              autoComplete="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              minLength={3}
            />
          </div>
          {mode === "register" && (
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-text-muted">Display name (optional)</label>
              <input
                className="input"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
              />
            </div>
          )}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-text-muted">Password</label>
            <input
              className="input"
              type="password"
              autoComplete={mode === "login" ? "current-password" : "new-password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
            />
          </div>
          {error && (
            <div className="rounded-md border border-status-error/40 bg-status-error/10 px-3 py-2 text-sm text-status-error">
              {error}
            </div>
          )}
          <button type="submit" disabled={submit.isPending} className="btn-primary w-full">
            {submit.isPending ? "…" : mode === "login" ? "Sign in" : "Create account"}
          </button>
        </form>
      </div>
    </div>
  );
}
